import React, { useEffect, useState } from 'react'
import swal from 'sweetalert';
import { useDebounce } from 'use-debounce';
import { errorHandler } from '@/config/errorReporting';
import { IUserBase } from '@/modules/users/domain/IUser';
import { IUserFilters, IUserTableFiltersProps } from '@/modules/users/domain/UsersTable';
import { getBaseList } from '@/modules/users/infraestructure/usersRequests';
import { Input } from '@umahealth/occipital/client';
import { auth } from '@/config/firebaseApp';
import { User } from 'firebase/auth';

const UsersTableFilters = ({ initialState, filters, setFilters, children }: IUserTableFiltersProps) => {
    const [internalState, setInternalState] = useState<IUserFilters>(initialState);
    const [fullnameDebouncedValue] = useDebounce(internalState.fullname, 1000);
    const [baseList, setBaseList] = useState<IUserBase[]>([]);

    const resetFilters = () => {
        setFilters(initialState);
        setInternalState(initialState);
    }
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const newFilter = { ...filters, [e.target.name]: e.target.value }

        // By default save input on internal component state
        setInternalState(newFilter)

        // if you need refresh on input change add input name property to array
        if(['base'].includes(e.target.name)){
            setFilters(newFilter); // Apply filter and fetch filtered data
        }
    }
    
    useEffect(() => {
        setFilters((prevState: IUserFilters) => {
            return { ...prevState, fullname: fullnameDebouncedValue }
        })
    }, [fullnameDebouncedValue, setFilters])

 useEffect(() => {
		if (auth?.currentUser) {
			const fetchBases = async ({
				currentUser,
			}: {
				currentUser: User;
			}) => {
				try {
					const baseListData = await getBaseList({
						currentUser: currentUser,
					});
					setBaseList(baseListData);
				} catch (error) {
					swal(
						"Ocurrió un problema",
						"No pudimos cargar la lista de bases, recarga la página o intenta más tarde",
						"warning"
					);
				}
			};

			fetchBases({
				currentUser: auth?.currentUser,
			});
		}
 }, []);

    useEffect(() => {
        setInternalState(filters)
    }, [filters]);
  return (
      <div className="flex gap-4 items-center">
          <div className="flex flex-col">
              <Input
                  label='Buscar por nombre, legajo o DNI'
                  type="text"
                  name="fullname"
                  placeholder="Nombre, legajo o DNI"
                  autoComplete="off"
                  id="search-driver"
				  className='w-72'
                  value={internalState.fullname ?? ''}
                  onChange={handleChange}
              />
          </div>

          {/* Ordenar y filtrar los resuelve el backend: la tabla muestra una
              página de 15, así que ordenar sólo lo visible daría un resultado
              engañoso. */}
          <div className="flex flex-col">
              <label htmlFor="orden-usuarios" className="text-xs text-grey-600 mb-1">
                  Ordenar por
              </label>
              <select
                  id="orden-usuarios"
                  className="h-10 px-3 rounded-lg border border-grey-200 text-sm"
                  value={`${filters.sort ?? 'lastName'}:${filters.order ?? 'asc'}`}
                  onChange={(e) => {
                      const [sort, order] = e.target.value.split(':') as [
                          'dt_create' | 'lastName',
                          'asc' | 'desc',
                      ];
                      setFilters((prev: IUserFilters) => ({ ...prev, sort, order }));
                  }}
              >
                  <option value="lastName:asc">Apellido (A-Z)</option>
                  <option value="lastName:desc">Apellido (Z-A)</option>
                  <option value="dt_create:desc">Alta, más recientes primero</option>
                  <option value="dt_create:asc">Alta, más antiguos primero</option>
              </select>
          </div>

          {/* Nómina de Trenes contra gente que usa la app. Con el padrón
              cargado son ~19.500 operadores contra un puñado de enfermeros y
              médicos: mezclados, encontrar una cuenta del portal se vuelve
              imposible. */}
          <div className="flex flex-col">
              <span className="text-xs text-grey-600 mb-1">Tipo de persona</span>
              <div className="flex h-10 rounded-lg border border-grey-200 overflow-hidden">
                  {([
                      { valor: undefined, etiqueta: 'Todos' },
                      { valor: 'nomina' as const, etiqueta: 'Nómina Trenes' },
                      { valor: 'app' as const, etiqueta: 'Usuarios de la app' },
                  ]).map(({ valor, etiqueta }) => {
                      const activo = filters.tipo === valor;
                      return (
                          <button
                              key={etiqueta}
                              type="button"
                              aria-pressed={activo}
                              onClick={() =>
                                  setFilters((prev: IUserFilters) => ({
                                      ...prev,
                                      tipo: valor,
                                  }))
                              }
                              className={`px-3 text-sm whitespace-nowrap border-r border-grey-200 last:border-r-0 ${
                                  activo
                                      ? 'bg-primary-700 text-white font-semibold'
                                      : 'bg-white text-grey-600 hover:bg-grey-50'
                              }`}
                          >
                              {etiqueta}
                          </button>
                      );
                  })}
              </div>
          </div>

          <div className="flex flex-col">
              <label htmlFor="estado-usuarios" className="text-xs text-grey-600 mb-1">
                  Estado
              </label>
              <select
                  id="estado-usuarios"
                  className="h-10 px-3 rounded-lg border border-grey-200 text-sm"
                  value={
                      filters.active === undefined
                          ? 'todos'
                          : filters.active
                            ? 'activos'
                            : 'inactivos'
                  }
                  onChange={(e) => {
                      const v = e.target.value;
                      setFilters((prev: IUserFilters) => ({
                          ...prev,
                          active: v === 'todos' ? undefined : v === 'activos',
                      }));
                  }}
              >
                  <option value="todos">Todos</option>
                  <option value="activos">Activos</option>
                  <option value="inactivos">Dados de baja</option>
              </select>
          </div>
      </div>
  )
}

export default UsersTableFilters